import { TimeUnit } from 'generated/graphql';
import {
  getCombinedQuantifyAndDashboardFilters,
  getQuantifyAggregatePeriodFromDuration,
  isValidQuery,
} from '../utils';
import { QuantifyActionType } from './quantifyActionType';
import { quantifyInitialState } from './quantifyInitialState';
import { QuantifyAction, QuantifyState } from './types';

export function quantifyReducer(
  state: QuantifyState,
  action: QuantifyAction,
): QuantifyState {
  const { type, payload } = action;
  switch (type) {
    case QuantifyActionType.INIT:
      return {
        ...state,
        ...payload,
      };
    case QuantifyActionType.INIT_QUERY: {
      let aggregatePeriod =
        payload?.aggregatePeriod ?? state.query.aggregatePeriod;

      if (payload?.duration) {
        aggregatePeriod = getQuantifyAggregatePeriodFromDuration({
          duration: payload.duration,
          currentAggregatePeriod: aggregatePeriod ?? TimeUnit.Month,
        });
      }

      const updatedQuery = {
        ...state.query,
        ...payload,
        formulaQuery: payload?.formulaQuery
          ? {
              ...state.query.formulaQuery,
              ...payload?.formulaQuery,
            }
          : state.query.formulaQuery,
        aggregatePeriod,
      };

      return {
        ...state,
        query: updatedQuery,
        visual: {
          ...state.visual,
          timeRangeTraversal: {
            originalDuration: payload?.duration ?? state.query.duration,
            traverseCount: 0,
          },
          hasQueryChanged: false,
          pageNumber: 1,
          quantifyAndDashboardFilters: getCombinedQuantifyAndDashboardFilters({
            primaryQuery: updatedQuery,
            dashboardFilters: state.visual.dashboardFilters,
          }),
        },
        reportData: { ...quantifyInitialState.reportData },
      };
    }
    case QuantifyActionType.SET_QUERY: {
      let aggregatePeriod =
        payload?.aggregatePeriod ?? state.query.aggregatePeriod;

      if (payload?.duration) {
        aggregatePeriod = getQuantifyAggregatePeriodFromDuration({
          duration: payload.duration,
          currentAggregatePeriod: aggregatePeriod ?? TimeUnit.Month,
        });
      }

      const updatedQuery = {
        ...state.query,
        ...payload,
        aggregatePeriod,
        formulaQuery: payload?.formulaQuery
          ? {
              ...state.query.formulaQuery,
              ...payload?.formulaQuery,
            }
          : state.query.formulaQuery,
        ...(payload?.compareMetadata
          ? {
              compareMetadata: {
                ...state.query.compareMetadata,
                ...payload.compareMetadata,
              },
            }
          : {}),
      };

      return {
        ...state,
        query: updatedQuery,
        visual: {
          ...state.visual,
          timeRangeTraversal: {
            originalDuration:
              payload?.duration ??
              state.visual.timeRangeTraversal.originalDuration,
            traverseCount: state.visual.timeRangeTraversal.traverseCount ?? 0,
          },
          hasQueryChanged: isValidQuery({ ...state.query, ...payload }),
          pageNumber: 1,
          searchTerm: '',
          quantifyAndDashboardFilters: getCombinedQuantifyAndDashboardFilters({
            primaryQuery: updatedQuery,
            dashboardFilters: state.visual.dashboardFilters,
          }),
          isTableQueryInProgress: !!payload?.sortBy,
        },
      };
    }
    case QuantifyActionType.RESET_QUERY: {
      return {
        query: { ...quantifyInitialState.query },
        visual: { ...quantifyInitialState.visual },
        reportData: { ...quantifyInitialState.reportData },
        misc: { ...quantifyInitialState.misc },
      };
    }

    case QuantifyActionType.SET_VISUAL: {
      return {
        ...state,
        visual: {
          ...state.visual,
          ...payload,
        },
      };
    }
    case QuantifyActionType.SET_REPORT_DATA: {
      const { payload } = action;

      return {
        ...state,
        reportData: {
          ...state.reportData,
          quantify: { ...state.reportData.quantify, ...payload.quantify },
          compare: { ...state.reportData.compare, ...payload.compare },
        },
      };
    }

    case QuantifyActionType.SET_MISC: {
      return {
        ...state,
        misc: {
          ...state.misc,
          ...payload,
        },
      };
    }

    default:
      return state;
  }
}
